import type { TranscriptUpdate } from "@frontierx/shared/schemas/TranscriptUpdate";

export interface TranscriptSentence {
  id: string;
  speaker_id: number;
  speaker: string;
  sentence: string;
  start_time: number;
  end_time: number;
  item_ids: string[];
}

export function processTranscriptItems(
  items: TranscriptUpdate[],
): TranscriptSentence[] {
  const sortedItems = items
    .filter((item) => item.words && item.words.length > 0)
    .sort((a, b) => a.words[0].start_time - b.words[0].start_time);

  const sentenceRegex = /[^.!?]+[.!?]+/g;

  let currentSpeakerId: number | null = null;
  let currentSentence = "";
  let currentWords: TranscriptUpdate["words"] = [];
  let currentItemIds: string[] = [];
  const sentences: TranscriptSentence[] = [];

  for (const item of sortedItems) {
    if (currentSpeakerId !== item.speaker_id) {
      // Process any remaining sentence from the previous speaker
      if (currentSentence.trim()) {
        addSentence(
          sentences,
          currentSpeakerId ?? 0,
          currentSentence,
          currentWords,
          currentItemIds,
          item.speaker,
        );
      }
      // Reset for the new speaker
      currentSpeakerId = item.speaker_id;
      currentSentence = "";
      currentWords = [];
      currentItemIds = [];
    }

    for (const word of item.words) {
      currentSentence += `${word.text} `;
      currentWords.push(word);
      if (!currentItemIds.includes(item.id)) {
        currentItemIds.push(item.id);
      }

      const matches = currentSentence.match(sentenceRegex);
      if (matches) {
        for (const match of matches) {
          addSentence(
            sentences,
            currentSpeakerId ?? 0,
            match,
            currentWords,
            [item.id], // Pass the current item ID for each sentence
            item.speaker,
          );
        }

        // Remove processed sentence and words
        currentSentence = currentSentence.replace(matches.join(""), "");
        currentWords = currentWords.filter(
          (w) => !matches.some((m) => m.includes(w.text)),
        );
        // Don't clear currentItemIds here - keep it for potential continuation
      }
    }
  }

  // Handle any remaining text as a sentence
  if (currentSentence.trim()) {
    addSentence(
      sentences,
      currentSpeakerId ?? 0,
      currentSentence,
      currentWords,
      currentItemIds,
      sortedItems[sortedItems.length - 1].speaker,
    );
  }

  // Sort the combined sentences by end_time
  const sortedResult = sentences.sort((a, b) => a.end_time - b.end_time);

  // Final validation: ensure all IDs are unique
  const ids = sortedResult.map((s) => s.id);
  const duplicateIds = ids.filter((id, index) => ids.indexOf(id) !== index);
  if (duplicateIds.length > 0) {
    console.error("Duplicate sentence IDs detected:", duplicateIds);
    // Fix duplicates by appending unique suffixes
    const seenIds = new Set<string>();
    sortedResult.forEach((sentence, _index) => {
      let uniqueId = sentence.id;
      let counter = 1;
      while (seenIds.has(uniqueId)) {
        uniqueId = `${sentence.id}-${counter}`;
        counter++;
      }
      sentence.id = uniqueId;
      seenIds.add(uniqueId);
    });
  }

  return sortedResult;
}

function addSentence(
  sentences: TranscriptSentence[],
  speakerId: number,
  sentenceText: string,
  words: TranscriptUpdate["words"],
  itemIds: string[],
  speaker: string,
) {
  const sentenceWords = words.filter((w) => sentenceText.includes(w.text));

  // Generate a unique ID that handles sentence splitting
  let uniqueId: string;
  if (itemIds.length > 0) {
    // Create a hash of the sentence text to make it unique when splitting
    const sentenceHash = sentenceText
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "")
      .substring(0, 8);
    uniqueId = `sentence-${itemIds[0]}-${sentenceHash}`;
  } else {
    // Fallback to timestamp-based ID with higher precision
    const baseId = `${speakerId}-${(sentenceWords[0]?.start_time ?? 0).toFixed(3)}-${(
      sentenceWords[sentenceWords.length - 1]?.end_time ?? 0
    ).toFixed(3)}`;

    // Add a unique counter to ensure uniqueness
    uniqueId = baseId;
    let counter = 1;
    while (sentences.some((s) => s.id === uniqueId)) {
      uniqueId = `${baseId}-${counter}`;
      counter++;
    }
  }

  sentences.push({
    id: uniqueId,
    speaker_id: speakerId,
    speaker: speaker,
    sentence: sentenceText.trim(),
    start_time: sentenceWords[0]?.start_time ?? 0,
    end_time: sentenceWords[sentenceWords.length - 1]?.end_time ?? 0,
    item_ids: [...itemIds],
  });
}
